import {Brick, registerBrick, ErrorFlow, BrickContext, Predicate, Transaction, QueryResult, Query} from 'olympe';
import Report from "../../../../models/Report";
import Observation from "../../../../models/Observation";
import Unit from "../../../../models/Unit";
import System from "../../../../models/System";
import Component from "../../../../models/Component";
import Inspection from "../../../../models/Inspection";

export default class ReportLinkMissingObservations extends Brick {

    /**
     * @override
     * @protected
     * @param {!BrickContext} $
     * @param {Report} report
     * @param {Unit} unit
     * @param {Array<Observation>} observations
     * @param {function()} forwardEvent
     * @param {function(ErrorFlow)} setErrorFlow
     */
    update($, [report, unit, observations], [forwardEvent, setErrorFlow]) {
        // Get all the entities that already have an observation in this unit to avoid duplicate
        const linkedObservations = new Set(Query.from(report)
            .follow(Report.observationsRel).andReturn()
            .follow(Unit.observationsRel.getInverse())
            .filter(Predicate.in(unit))
            .executeFromCache().keys());

        const transaction = new Transaction();

        observations.flatMap((observation) => {
            // Add observations from children and ancestors of the entities.
            const children = ReportLinkMissingObservations.getObservationChildren(unit, observation);
            const parents = ReportLinkMissingObservations.getParents(unit, observation);
            return children.concat(parents.slice(1));
        }).filter((observation) => {
            // Do not create observations for entities that already have one.
            return !linkedObservations.has(observation.getTag());
        }).forEach((observation) => {
            transaction.createRelation(Report.observationsRel, report, observation);
        });

        transaction.execute().then(forwardEvent).catch((e) => {
            setErrorFlow(ErrorFlow.create(`Failed to add observations to the report: ${e}`, 0));
        });
    }

    /**
     * @param {Unit} unit
     * @param {Observation} observation
     * @return {Observation[]}
     */
    static getObservationChildren(unit, observation) {
        const entity = observation.followSingle(Observation.targetRel).executeFromCache();
        let children = [];
        if (entity instanceof System) {
            const components = entity.follow(System.componentsRel).executeFromCache();
            children = ReportLinkMissingObservations.entitiesToObservations(unit, components)
                .flatMap((obs) => ReportLinkMissingObservations.getObservationChildren(unit, obs));
        }

        else if (entity instanceof Component) {
            const subcomponents = entity.follow(Component.subComponentsRel).executeFromCache();
            if (subcomponents.size() > 0) {
                children = ReportLinkMissingObservations.entitiesToObservations(unit, subcomponents)
                    .flatMap((obs) => ReportLinkMissingObservations.getObservationChildren(unit, obs));
            }
            // No subcomponents => It is a leaf component so get inspections.
            else {
                const inspections = entity.follow(Component.inspectionsRel).executeFromCache();
                children = ReportLinkMissingObservations.entitiesToObservations(unit, inspections)
                    .flatMap((obs) => ReportLinkMissingObservations.getObservationChildren(unit, obs));
            }
        }

        else if (entity instanceof Inspection) {
            children = []; // No more children
        }

        return [observation].concat(children);
    }

    /**
     * @param {Unit} unit
     * @param {Observation} observation
     * @return {Array<Observation>}
     */
    static getParents(unit, observation) {
        const entity = observation.followSingle(Observation.targetRel).executeFromCache();

        let parents = [];
        if (entity instanceof Inspection) {
            const parentResult = entity.follow(Component.inspectionsRel.getInverse()).executeFromCache();
            const parentObservation = ReportLinkMissingObservations.entitiesToObservations(unit, parentResult)[0];
            parents = parentObservation ? ReportLinkMissingObservations.getParents(unit, parentObservation) : [];
        }

        else if (entity instanceof Component) {
            const parentObservation = [
                ...ReportLinkMissingObservations.entitiesToObservations(unit, entity.follow(Component.subComponentsRel.getInverse()).executeFromCache()),
                ...ReportLinkMissingObservations.entitiesToObservations(unit, entity.follow(System.componentsRel.getInverse()).executeFromCache())
            ][0];
            parents = parentObservation ? ReportLinkMissingObservations.getParents(unit, parentObservation) : [];
        }

        return [observation].concat(parents);
    }

    /**
     * @private
     * @param {Unit} unit
     * @param {QueryResult<InspectionEntity>} entities
     * @return {Observation[]}
     */
    static entitiesToObservations(unit, entities) {
        return entities.reduce((array, entity) => {
            const obs = entity.follow(Observation.targetRel.getInverse()).andReturn()
                .follow(Unit.observationsRel.getInverse())
                .filter(Predicate.in(unit))
                .executeFromCache()
                .getFirst();
            obs && array.push(obs);
            return array;
        }, []);
    }
}

registerBrick('018adc29550bd3787276', ReportLinkMissingObservations);
